import { ref, onMounted, onUnmounted, nextTick } from '#imports';
import { useI18n } from 'vue-i18n';
import { useToast } from '~/composables/useToast';

/**
 * 通用分页加载 Composable
 * @template T 数据项类型
 * @param fetchFunction 获取数据的API函数
 * @param options 配置项
 */
export function usePagination<T>(
  fetchFunction: (page: number, ...args: any[]) => Promise<any>,
  options: {
    pageSize?: number;
    initialPage?: number;
    dataExtractor?: (response: any) => T[];
    totalExtractor?: (response: any) => number;
    processItem?: (item: T) => T;
    errorHandler?: (error: any) => void;
    autoLoad?: boolean;
    preserveListOnReload?: boolean;
  } = {}
) {
  const { t } = useI18n();
  const toast = useToast();

  // 默认选项
  const defaultOptions = {
    pageSize: 20,
    initialPage: 1,
    dataExtractor: (response: any) => {
      // 简化默认数据提取逻辑，保持通用性
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.list)) {
        return response.data.list;
      }
      return [];
    },
    totalExtractor: (response: any) => {
      // 默认总数提取逻辑
      if (response.data && typeof response.data.total === 'number') {
        return response.data.total;
      } else if (typeof response.total === 'number') {
        return response.total;
      }
      return 0;
    },
    processItem: (item: T) => item,
    errorHandler: (error: any) => {
      console.error('加载数据失败:', error);
      toast.error(t('common.load_failed'));
    },
    autoLoad: true,
    preserveListOnReload: false,
  };

  // 合并选项
  const mergedOptions = { ...defaultOptions, ...options };

  // 状态
  const list = ref<T[]>([]);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const page = ref(mergedOptions.initialPage);
  const total = ref(0);
  const hasMore = ref(true);
  const error = ref<string | null>(null);
  const loadMoreTrigger = ref<HTMLElement | null>(null);
  const observerInitialized = ref(false);

  // 加载数据
  const loadData = async (pageNum = mergedOptions.initialPage, ...args: any[]) => {
    // 防止重复加载
    if (isLoading.value && pageNum === mergedOptions.initialPage) return;
    if (isLoadingMore.value && pageNum > mergedOptions.initialPage) return;

    // 设置加载状态
    if (pageNum === mergedOptions.initialPage) {
      isLoading.value = true;
      // 重置分页状态
      page.value = mergedOptions.initialPage;
      // 只有在不保留列表数据时才清空列表
      if (!mergedOptions.preserveListOnReload) {
        list.value = [];
      }
    } else {
      isLoadingMore.value = true;
    }

    error.value = null;

    try {
      // 调用API函数获取数据
      const response = await fetchFunction(pageNum, ...args);

      // 提取数据和总数
      const items = mergedOptions.dataExtractor(response);
      const totalCount = mergedOptions.totalExtractor(response);
      // 处理每个数据项
      const processedItems = (items || []).map((item: any) => mergedOptions.processItem(item));

      // 更新列表
      if (pageNum === mergedOptions.initialPage) {
        list.value = processedItems;
      } else {
        // 确保不重复添加相同的数据
        const existingIds = new Set(list.value.map((item: any) => item.id));
        const uniqueNewItems = processedItems.filter((item: any) => !existingIds.has(item.id));

        if (uniqueNewItems.length > 0) {
          list.value = [...list.value, ...uniqueNewItems];
        } else {
          // 如果没有新数据，可能已经到达末尾
          hasMore.value = false;
        }
      }

      // 更新分页状态
      page.value = pageNum;
      total.value = totalCount;

      // 检查是否还有更多数据
      if (totalCount > 0) {
        hasMore.value = list.value.length < totalCount;
      } else {
        hasMore.value = processedItems.length >= mergedOptions.pageSize;
      }

    } catch (err) {
      // 处理错误
      if (mergedOptions.errorHandler) {
        mergedOptions.errorHandler(err);
      }
      
      error.value = t('common.load_failed');
      
      // 如果是初始加载失败，重置列表
      if (pageNum === mergedOptions.initialPage) {
        list.value = [];
      }
      
      // 如果已经加载了一些数据，但加载更多失败，设置hasMore为false
      if (list.value.length > 0) {
        hasMore.value = false;
      }
    } finally {
      // 重置加载状态
      if (pageNum === mergedOptions.initialPage) {
        isLoading.value = false;
      } else {
        isLoadingMore.value = false;
      }
    }
  };

  // 加载更多数据
  const loadMore = async (...args: any[]) => {
    if (isLoading.value || isLoadingMore.value || !hasMore.value) {
      return;
    }

    const nextPage = page.value + 1;
    await loadData(nextPage, ...args);
  };

  // 获取文档高度的可靠方法
  const getDocumentHeight = () => {
    return Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );
  };

  // 设置无限滚动
  const setupInfiniteScroll = () => {
    if (typeof window === 'undefined') return () => {};

    // 获取窗口高度，计算相对阈值
    const windowHeight = window.innerHeight;
    const relativeRootMargin = Math.round(windowHeight * 0.3);

    // 统一滚动检测函数
    const handleScroll = () => {
      if (isLoading.value || isLoadingMore.value || !hasMore.value || list.value.length === 0) return;

      const scrollPosition = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = getDocumentHeight();
      
      // 使用相对窗口高度的阈值
      const scrollThreshold = Math.round(windowHeight * 0.3);
      const hasReachedThreshold = scrollPosition + windowHeight >= documentHeight - scrollThreshold;

      if (hasReachedThreshold) {
        loadMore();
      }
    };

    // 初始检查 - 检查是否需要立即加载更多
    const checkInitialLoad = () => {
      if (!mergedOptions.autoLoad) return;
      
      // 检查当前内容是否填满页面
      const windowHeight = window.innerHeight;
      const documentHeight = getDocumentHeight();
      
      if (documentHeight < windowHeight * 1.5 && hasMore.value && !isLoading.value && !isLoadingMore.value && list.value.length > 0) {
        loadMore();
      }
    };

    // 创建和初始化 Intersection Observer
    let observer: IntersectionObserver | null = null;
    
    const initIntersectionObserver = () => {
      if (observerInitialized.value) return;
      
      if (!loadMoreTrigger.value) return;

      // 创建观察者
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isLoading.value && !isLoadingMore.value && hasMore.value && list.value.length > 0) {
            loadMore();
          }
        });
      }, {
        root: null, 
        rootMargin: `0px 0px ${relativeRootMargin}px 0px`,
        threshold: 0.1
      });
      
      // 开始观察触发器元素
      observer.observe(loadMoreTrigger.value);
      observerInitialized.value = true;
    };

    // 使用 MutationObserver 监听 DOM 变化，等待 loadMoreTrigger 出现
    const mutationObserver = new MutationObserver(() => {
      if (loadMoreTrigger.value && !observerInitialized.value) {
        initIntersectionObserver();
      }
    });
    
    // 添加滚动事件监听（作为备用机制）
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // 添加调整窗口大小事件处理
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // 启动 DOM 变化监听
    mutationObserver.observe(document.body, { 
      childList: true, 
      subtree: true 
    });

    // 在下一个渲染帧检查 loadMoreTrigger 是否存在
    nextTick(() => {
      if (loadMoreTrigger.value) {
        initIntersectionObserver();
      }
      
      // 检查初始加载状态
      checkInitialLoad();
    });

    // 返回清理函数
    return () => {
      if (observer && loadMoreTrigger.value) {
        observer.unobserve(loadMoreTrigger.value);
      }
      if (observer) {
        observer.disconnect();
      }
      mutationObserver.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  };

  // 重置并重新加载
  const reload = async (...args: any[]) => {
    await loadData(mergedOptions.initialPage, ...args);
  };

  // 在组件挂载后自动加载第一页数据
  onMounted(() => {
    if (mergedOptions.autoLoad) {
      loadData(mergedOptions.initialPage);
    }

    // 设置无限滚动
    let cleanup: (() => void) | null = null;
    cleanup = setupInfiniteScroll();

    // 组件卸载时清理
    onUnmounted(() => {
      if (cleanup) {
        cleanup();
      }
    });
  });

  return {
    list,
    isLoading,
    isLoadingMore,
    page,
    total,
    hasMore,
    error,
    loadMoreTrigger,
    loadData,
    loadMore,
    reload
  };
}
